import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { contactSchema, type ContactFormData } from '../lib/validation';
import { useNotification } from '../context/NotificationContext';
import FormError from '../components/FormError';

function Contact() {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});

  const validateForm = () => {
    try {
      contactSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error) {
        const zodError = JSON.parse(error.message);
        const newErrors: typeof errors = {};
        zodError.forEach((err: any) => {
          const path = err.path[0] as keyof ContactFormData;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showNotification('error', 'Please fix the form errors before submitting');
      return;
    }

    setLoading(true);

    try {
      // Send email using mailto link as a fallback
      const subject = `Contact Form: ${formData.name}`;
      const body = `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage: ${formData.message}`;
      
      // Open mailto link in a new tab
      window.open(`mailto:aussicleaning@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
      
      showNotification('success', 'Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      showNotification('error', 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0efe9] dark:bg-gray-900">
      {/* Hero Section */}
      <div className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-light text-gray-800 dark:text-white mb-6">Contact Us</h1>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Get in touch with our team for any questions or concerns
            </p>
          </div>
        </div>
      </div>

      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8">
              <h2 className="text-2xl font-light text-gray-800 dark:text-white mb-6">Send us a Message</h2>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-2 border focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300 transition-all duration-200 bg-white dark:bg-gray-700 ${
                      errors.name ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="John Doe"
                  />
                  {errors.name && <FormError message={errors.name} />}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-2 border focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300 transition-all duration-200 bg-white dark:bg-gray-700 ${
                      errors.email ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="john@example.com"
                  />
                  {errors.email && <FormError message={errors.email} />}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                    className={`w-full px-4 py-2 border focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300 transition-all duration-200 bg-white dark:bg-gray-700 ${
                      errors.phone ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="(240) 289-3658"
                    maxLength={10}
                  />
                  {errors.phone && <FormError message={errors.phone} />}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className={`w-full px-4 py-2 border focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300 transition-all duration-200 bg-white dark:bg-gray-700 ${
                      errors.message ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="How can we help you?"
                  />
                  {errors.message && <FormError message={errors.message} />}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-gray-800 text-white dark:bg-gray-700 hover:bg-black dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="mb-12">
                <h2 className="text-2xl font-light text-gray-800 dark:text-white mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-gray-800 dark:text-gray-300 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-800 dark:text-white mb-1">Phone</h3>
                      <a href="tel:240-289-3658" className="text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white">
                        240-289-3658
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-gray-800 dark:text-gray-300 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-800 dark:text-white mb-1">Email</h3>
                      <a href="mailto:aussicleaning@gmail.com" className="text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white">
                        aussicleaning@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-gray-800 dark:text-gray-300 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-gray-800 dark:text-white mb-1">Location</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        3350 Huntley Square Dr<br />Temple Hills, Maryland 20748
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8">
                <h2 className="text-2xl font-light text-gray-800 dark:text-white mb-6">Service Area</h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  We proudly serve Maryland, DC, and Northern Virginia
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    "Temple Hills", "Washington DC", "Alexandria", "Arlington",
                    "Silver Spring", "Bethesda", "Rockville", "Fairfax"
                  ].map((area, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-300" />
                      <span className="text-gray-600 dark:text-gray-300">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="py-12 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-light text-gray-800 dark:text-white mb-6 text-center">Our Location</h2>
            <div className="h-[400px] bg-gray-200 dark:bg-gray-700">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.8046853696745!2d-76.95161372374644!3d38.83893595223027!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7a9b3f5d4be8f%3A0x1b0e0f1b0e0f1b0e!2s3350%20Huntley%20Square%20Dr%2C%20Temple%20Hills%2C%20MD%2020748!5e0!3m2!1sen!2sus!4v1645564944227!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Aussi Clean Location"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 bg-gray-800 dark:bg-gray-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-light mb-6">Ready for a Spotless Space?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your cleaning service today and enjoy a pristine environment. Satisfaction guaranteed!
          </p>
          <Link
            to="/book"
            className="inline-block px-8 py-3 border-2 border-white hover:bg-white hover:text-gray-800 transition-colors"
          >
            Book Now
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Contact;